import { motion } from "framer-motion";
import { Search, Target, Trophy, Sparkles, PlusCircle, Percent } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";

const HowItWorks = () => {
  const { t, locale } = useLocale();

  const steps = [
    {
      icon: Search,
      title: t("how.step2.title"),
      description: t("how.step2.description"),
      color: "text-primary",
    },
    {
      icon: Target,
      title: t("how.step3.title"),
      description: t("how.step3.description"),
      color: "text-success",
    },
    {
      icon: Trophy,
      title: t("how.step4.title"),
      description: t("how.step4.description"),
      color: "text-accent",
    },
    {
      icon: PlusCircle,
      title: t("how.step5.title"),
      description: t("how.step5.description"),
      color: "text-primary",
    },
  ];

  return (
    <section id="how-it-works" className="py-24 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/20 to-background" />
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-medium mb-4">
            <Sparkles size={16} />
            {t("how.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {t("how.title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("how.description")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative"
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-1/2 w-full h-px bg-gradient-to-r from-border to-transparent" />
              )}
              
              <div className="glass-card p-5 text-center h-full relative">
                {/* Step number */}
                <div className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </div>
                
                <div className={`w-14 h-14 mx-auto mb-3 rounded-xl bg-secondary/50 flex items-center justify-center ${step.color}`}>
                  <step.icon size={24} />
                </div>
                
                <h3 className="text-base font-bold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Fee Structure Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          <div className="glass-card p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Percent size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground">
                  {locale.language === "pt" ? "Distribuição das Recompensas" : "Reward Distribution"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {locale.language === "pt" 
                    ? "Como o pool dos perdedores é distribuído" 
                    : "How the losing pool is distributed"}
                </p>
              </div>
            </div>

            {/* Visual Bar */}
            <div className="mb-6">
              <div className="h-8 rounded-full overflow-hidden flex">
                <div className="h-full bg-gradient-to-r from-success to-success/80 flex items-center justify-center text-xs font-bold text-success-foreground" style={{ width: "93%" }}>
                  93%
                </div>
                <div className="h-full bg-gradient-to-r from-primary to-primary/80 flex items-center justify-center text-xs font-bold text-primary-foreground" style={{ width: "5%" }}>
                  5%
                </div>
                <div className="h-full bg-gradient-to-r from-accent to-accent/80 flex items-center justify-center text-xs font-bold text-accent-foreground" style={{ width: "2%" }}>
                  2%
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-success/10 border border-success/30">
                <div className="w-4 h-4 rounded-full bg-success" />
                <div>
                  <p className="font-bold text-foreground">93%</p>
                  <p className="text-xs text-muted-foreground">
                    {locale.language === "pt" ? "Dividido entre Vencedores" : "Split Between Winners"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/10 border border-primary/30">
                <div className="w-4 h-4 rounded-full bg-primary" />
                <div>
                  <p className="font-bold text-foreground">5%</p>
                  <p className="text-xs text-muted-foreground">
                    {locale.language === "pt" ? "Taxa Callyout" : "Callyout Fee"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/10 border border-accent/30">
                <div className="w-4 h-4 rounded-full bg-accent" />
                <div>
                  <p className="font-bold text-foreground">2%</p>
                  <p className="text-xs text-muted-foreground">
                    {locale.language === "pt" ? "Criador do Evento" : "Event Creator"}
                  </p>
                </div>
              </div>
            </div>

            {/* Proportional split explanation */}
            <div className="mt-4 p-3 rounded-lg bg-success/5 border border-success/20">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-success">
                  {locale.language === "pt" ? "Como funciona a divisão: " : "How the split works: "}
                </span>
                {locale.language === "pt"
                  ? "Os 93% são divididos proporcionalmente com base no valor previsto. Se você previu 100 CallyCoins e o total dos vencedores foi 1000 CallyCoins, você recebe 10% dos 93%."
                  : "The 93% is split proportionally based on your prediction amount. If you predicted 100 CallyCoins and the total winning pool is 1000 CallyCoins, you receive 10% of the 93%."}
              </p>
            </div>

            <p className="text-xs text-muted-foreground mt-4 text-center">
              {locale.language === "pt"
                ? "* Em eventos oficiais do Callyout, a plataforma recebe 7% (sem taxa do criador)"
                : "* For official Callyout events, the platform receives 7% (no creator fee)"}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
